import { useCallback } from "react"
import {
  ConnectorFieldMapping,
  ConnectorFieldMappingEntry
} from "../../../../../../types"

interface UseValueTransformsProps {
  fieldMapping: ConnectorFieldMapping
  isIncoming: boolean
  onChange: (mapping: ConnectorFieldMapping) => void
}

export const useValueTransforms = ({
  fieldMapping,
  isIncoming,
  onChange
}: UseValueTransformsProps) => {
  const getValueTransforms = useCallback(
    (entry: ConnectorFieldMappingEntry): Record<string, any> => {
      return entry.values || {}
    },
    []
  )

  const updateValueTransform = useCallback(
    (index: number, sourceValue: string, targetValue: string) => {
      const newMapping = [...fieldMapping]
      const entry = { ...newMapping[index] }
      const values: Record<string, any> = { ...(entry.values || {}) }

      if (targetValue) {
        if (sourceValue === "default") {
          values["default"] = targetValue
        } else if (isIncoming) {
          // For incoming: sourceValue is BB value (from option.value), targetValue is CRM value
          // Store as: CRM value -> BB value (keys are CRM values = source of incoming data)
          // First remove any existing CRM key that maps to this same BB value
          Object.entries(values).forEach(([k, v]) => {
            if (v === sourceValue && k !== "default") delete values[k]
          })
          values[targetValue] = sourceValue
        } else {
          // For outgoing: sourceValue is BB value, targetValue is CRM value
          // Store as: BB value -> CRM value (keys are BB values = source of outgoing data)
          values[sourceValue] = targetValue
        }
      } else {
        // Removing
        if (isIncoming) {
          // Find and remove the entry where value === sourceValue
          Object.entries(values).forEach(([k, v]) => {
            if (v === sourceValue) delete values[k]
          })
        } else {
          delete values[sourceValue]
        }
      }

      entry.values = Object.keys(values).length > 0 ? values : undefined

      newMapping[index] = entry
      onChange(newMapping)
    },
    [fieldMapping, isIncoming, onChange]
  )

  const removeValueTransform = useCallback(
    (index: number, sourceValue: string) => {
      const newMapping = [...fieldMapping]
      const entry = { ...newMapping[index] }

      if (entry.values) {
        const values = { ...entry.values }
        delete values[sourceValue]
        entry.values = Object.keys(values).length > 0 ? values : undefined
      }

      newMapping[index] = entry
      onChange(newMapping)
    },
    [fieldMapping, onChange]
  )

  const removeAllValueTransforms = useCallback(
    (index: number) => {
      const newMapping = [...fieldMapping]
      const entry = { ...newMapping[index] }
      delete entry.values
      newMapping[index] = entry
      onChange(newMapping)
    },
    [fieldMapping, onChange]
  )

  return {
    getValueTransforms,
    updateValueTransform,
    removeValueTransform,
    removeAllValueTransforms
  }
}
